"use client";

import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import { Settings, Search, Bot, Plus, Pin, Calendar, CalendarDays, Bell, Clock, ClipboardList, MessageCircle, Users, BarChart3, ExternalLink, Keyboard, Rss, TrendingUp, Sparkles, ListTodo, MessagesSquare, Mail, Inbox, Library, Workflow } from "lucide-react";
import dynamic from "next/dynamic";
import type { WidgetFieldSchema } from "@/lib/widgets/widgetSettings";
export type { WidgetFieldSchema } from "@/lib/widgets/widgetSettings";

// ─── Lazy-loaded widget panels (code-split per widget) ──────
/* eslint-disable @typescript-eslint/no-explicit-any */
const DiagramAssistantPanel = dynamic(() => import("@/components/diagrams/DiagramAssistantPanel").then(m => ({ default: m.DiagramAssistantPanel })), { ssr: false });
const SearchPanel = dynamic(() => import("./SearchWidget").then((m) => ({ default: m.SearchPanel })), { ssr: false }) as ComponentType<any>;
const SearchBarContent = dynamic(() => import("./SearchWidget").then((m) => ({ default: m.SearchBarContent })), { ssr: false }) as ComponentType<any>;

const AIPanel = dynamic(() => import("./AIWidget").then((m) => ({ default: m.AIPanel })), { ssr: false }) as ComponentType<any>;
const AIBarContent = dynamic(() => import("./AIWidget").then((m) => ({ default: m.AIBarContent })), { ssr: false }) as ComponentType<any>;

const QuickCreatePanel = dynamic(() => import("./QuickCreateWidget").then((m) => ({ default: m.QuickCreatePanel })), { ssr: false }) as ComponentType<any>;
const QuickCreateBarContent = dynamic(() => import("./QuickCreateWidget").then((m) => ({ default: m.QuickCreateBarContent })), { ssr: false }) as ComponentType<any>;

const FavoritesPanel = dynamic(() => import("./FavoritesWidget").then((m) => ({ default: m.FavoritesPanel })), { ssr: false }) as ComponentType<any>;
const FavoritesBarContent = dynamic(() => import("./FavoritesWidget").then((m) => ({ default: m.FavoritesBarContent })), { ssr: false }) as ComponentType<any>;

const TodayPanel = dynamic(() => import("./TodayWidget").then((m) => ({ default: m.TodayPanel })), { ssr: false }) as ComponentType<any>;
const TodayBarContent = dynamic(() => import("./TodayWidget").then((m) => ({ default: m.TodayBarContent })), { ssr: false }) as ComponentType<any>;

const NotificationsPanel = dynamic(() => import("./NotificationsWidget").then((m) => ({ default: m.NotificationsPanel })), { ssr: false }) as ComponentType<any>;
const NotificationsBarContent = dynamic(() => import("./NotificationsWidget").then((m) => ({ default: m.NotificationsBarContent })), { ssr: false }) as ComponentType<any>;

const TimerPanel = dynamic(() => import("./TimerWidget").then((m) => ({ default: m.TimerPanel })), { ssr: false }) as ComponentType<any>;
const TimerBarContent = dynamic(() => import("./TimerWidget").then((m) => ({ default: m.TimerBarContent })), { ssr: false }) as ComponentType<any>;

const ClipboardPanel = dynamic(() => import("./ClipboardWidget").then((m) => ({ default: m.ClipboardPanel })), { ssr: false }) as ComponentType<any>;
const ClipboardBarContent = dynamic(() => import("./ClipboardWidget").then((m) => ({ default: m.ClipboardBarContent })), { ssr: false }) as ComponentType<any>;

const SettingsPanel = dynamic(() => import("./SettingsWidget").then((m) => ({ default: m.SettingsPanel })), { ssr: false }) as ComponentType<any>;
const SettingsBarContent = dynamic(() => import("./SettingsWidget").then((m) => ({ default: m.SettingsBarContent })), { ssr: false }) as ComponentType<any>;

const ShortcutsPanel = dynamic(() => import("./ShortcutsWidget").then((m) => ({ default: m.ShortcutsPanel })), { ssr: false }) as ComponentType<any>;
const ShortcutsBarContent = dynamic(() => import("./ShortcutsWidget").then((m) => ({ default: m.ShortcutsBarContent })), { ssr: false }) as ComponentType<any>;

const CapabilitiesPanel = dynamic(() => import("./CapabilitiesWidget").then((m) => ({ default: m.CapabilitiesPanel })), { ssr: false }) as ComponentType<any>;
const CapabilitiesBarContent = dynamic(() => import("./CapabilitiesWidget").then((m) => ({ default: m.CapabilitiesBarContent })), { ssr: false }) as ComponentType<any>;

const PageGuidePanel = dynamic(() => import("./PageGuideWidget").then((m) => ({ default: m.PageGuidePanel })), { ssr: false }) as ComponentType<any>;
const PageGuideRenderBar = dynamic(() => import("./PageGuideWidget").then((m) => ({ default: m.PageGuideRenderBar })), { ssr: false }) as ComponentType<any>;

const WeeklyPlannerPanel = dynamic(() => import("./WeeklyPlannerWidget").then((m) => ({ default: m.WeeklyPlannerPanel })), { ssr: false }) as ComponentType<any>;
const WeeklyPlannerBarContent = dynamic(() => import("./WeeklyPlannerWidget").then((m) => ({ default: m.WeeklyPlannerBarContent })), { ssr: false }) as ComponentType<any>;

const KPIPanel = dynamic(() => import("./KPIWidget").then((m) => ({ default: m.KPIPanel })), { ssr: false }) as ComponentType<any>;
const KPIBarContent = dynamic(() => import("./KPIWidget").then((m) => ({ default: m.KPIBarContent })), { ssr: false }) as ComponentType<any>;

const ExternalLinksPanel = dynamic(() => import("./ExternalLinksWidget").then((m) => ({ default: m.ExternalLinksPanel })), { ssr: false }) as ComponentType<any>;
const ExternalLinksBarContent = dynamic(() => import("./ExternalLinksWidget").then((m) => ({ default: m.ExternalLinksBarContent })), { ssr: false }) as ComponentType<any>;

const WATIPanel = dynamic(() => import("./WATIWidget").then((m) => ({ default: m.WATIPanel })), { ssr: false }) as ComponentType<any>;
const WATIBarContent = dynamic(() => import("./WATIWidget").then((m) => ({ default: m.WATIBarContent })), { ssr: false }) as ComponentType<any>;

const WATIIframePanel = dynamic(() => import("./WATIIframeWidget").then((m) => ({ default: m.WATIIframePanel })), { ssr: false }) as ComponentType<any>;
const WATIIframeBarContent = dynamic(() => import("./WATIIframeWidget").then((m) => ({ default: m.WATIIframeBarContent })), { ssr: false }) as ComponentType<any>;

const TeamPanel = dynamic(() => import("./TeamWidget").then((m) => ({ default: m.TeamPanel })), { ssr: false }) as ComponentType<any>;
const TeamBarContent = dynamic(() => import("./TeamWidget").then((m) => ({ default: m.TeamBarContent })), { ssr: false }) as ComponentType<any>;

const RssPanel = dynamic(() => import("./RssWidget").then((m) => ({ default: m.RssPanel })), { ssr: false }) as ComponentType<any>;
const RssBarContent = dynamic(() => import("./RssWidget").then((m) => ({ default: m.RssBarContent })), { ssr: false }) as ComponentType<any>;

const LeadsPipelinePanel = dynamic(() => import("./LeadsPipelineWidget").then((m) => ({ default: m.LeadsPipelinePanel })), { ssr: false }) as ComponentType<any>;
const LeadsPipelineBarContent = dynamic(() => import("./LeadsPipelineWidget").then((m) => ({ default: m.LeadsPipelineBarContent })), { ssr: false }) as ComponentType<any>;

const MatchingPanel = dynamic(() => import("./MatchingWidget").then((m) => ({ default: m.MatchingPanel })), { ssr: false }) as ComponentType<any>;
const MatchingBarContent = dynamic(() => import("./MatchingWidget").then((m) => ({ default: m.MatchingBarContent })), { ssr: false }) as ComponentType<any>;

const CeoQueuePanel = dynamic(() => import("./CeoQueueWidget").then((m) => ({ default: m.CeoQueuePanel })), { ssr: false }) as ComponentType<any>;
const CeoQueueBarContent = dynamic(() => import("./CeoQueueWidget").then((m) => ({ default: m.CeoQueueBarContent })), { ssr: false }) as ComponentType<any>;

const CommunicationWidgetPanel = dynamic(() => import("./CommunicationWidget").then((m) => ({ default: m.CommunicationWidgetPanel })), { ssr: false }) as ComponentType<any>;
const CommunicationBarContent = dynamic(() => import("./CommunicationWidget").then((m) => ({ default: m.CommunicationBarContent })), { ssr: false }) as ComponentType<any>;

const EmailStatsPanel = dynamic(() => import("@/components/email/EmailStatsWidget").then((m) => ({ default: m.EmailStatsPanel })), { ssr: false }) as ComponentType<any>;
const EmailStatsBarContent = dynamic(() => import("@/components/email/EmailStatsWidget").then((m) => ({ default: m.EmailStatsBarContent })), { ssr: false }) as ComponentType<any>;

const GmailPanel = dynamic(() => import("./GmailWidget").then((m) => ({ default: m.GmailPanel })), { ssr: false }) as ComponentType<any>;
const GmailBarContent = dynamic(() => import("./GmailWidget").then((m) => ({ default: m.GmailBarContent })), { ssr: false }) as ComponentType<any>;

const CalendarWidgetPanel = dynamic(() => import("./CalendarWidget").then((m) => ({ default: m.CalendarPanel })), { ssr: false }) as ComponentType<any>;
const CalendarBarContent = dynamic(() => import("./CalendarWidget").then((m) => ({ default: m.CalendarBarContent })), { ssr: false }) as ComponentType<any>;
/* eslint-enable @typescript-eslint/no-explicit-any */
 


export type WidgetSize = 1 | 2 | 3 | 4;

export type WidgetCategory =
  | "basics"
  | "productivity"
  | "ai_comms"
  | "team"
  | "analytics"
  | "integrations";

export type WidgetTier = "free" | "pro" | "business";

export type WidgetPlacement = "toolbar" | "apps" | "disabled";

export interface WidgetDefinition {
  id: string;
  icon: LucideIcon;
  label: { he: string; en: string; ru: string };
  description: { he: string; en: string; ru: string };
  defaultSize: WidgetSize;
  status: "active" | "coming-soon";
  category: WidgetCategory;
  tier: WidgetTier;
  /** false = system widget (Settings, Search) — always toolbar, cannot disable */
  isRemovable: boolean;
  /** Content rendered inside the dropdown panel (not required for coming-soon) */
  component?: ComponentType;
  /** Inline content rendered in the top bar when size >= 2 */
  renderBar?: ComponentType<{ size: WidgetSize }>;
  /** "modal" = centered overlay, "side-panel" = sliding side panel */
  panelMode?: "dropdown" | "modal" | "side-panel";
  /** Which chrome frame this widget lives in. "topbar" = horizontal strip at
   *  the top (existing behavior). "sidebar-rail" = vertical rail on the LEFT
   *  edge that opens an adjacent panel rendering the widget's full UI inline
   *  (notifications, AI chat, etc.). "both" = appears in both. Defaults to
   *  "topbar" so existing widgets are unaffected. */
  placement?: "topbar" | "sidebar-rail" | "both";
  /** Optional schema-driven user settings. When present, SidebarWidgetInspector
   *  renders a WidgetSettingsEditor for these fields. Values persist via
   *  src/lib/widgets/widgetSettings.ts (localStorage + change event). Widgets
   *  consume their own settings at runtime via useWidgetSettings(id, schema). */
  settingsSchema?: WidgetFieldSchema[];
}

/** Resolve effective placement for a widget, enforcing system widget invariant */
export function getEffectivePlacement(
  id: string,
  placements: Record<string, WidgetPlacement>,
  isRemovable: boolean
): WidgetPlacement {
  if (!isRemovable) return "toolbar";
  return placements[id] ?? "toolbar";
}

/** All registered widgets. Add new widgets here. */
export const widgetRegistry: WidgetDefinition[] = [
  {
    id: "diagram-assistant", icon: Workflow,
    label: { he: "צור דיאגרמה", en: "Create diagram", ru: "Создать диаграмму" },
    description: { he: "יצירת תרשים ממקורות נבחרים ושיחה מתמשכת", en: "Create a diagram from selected sources with persistent conversation", ru: "Диаграмма из выбранных источников" },
    defaultSize: 1, status: "active", category: "ai_comms", tier: "free", isRemovable: true,
    component: DiagramAssistantPanel, panelMode: "side-panel", placement: "topbar",
  },
  {
    id: "search",
    icon: Search,
    label: { he: "חיפוש", en: "Search", ru: "Поиск" },
    description: {
      he: "חיפוש פרויקטים, מסמכים ושיחות",
      en: "Search projects, documents & conversations",
      ru: "Поиск проектов, документов и разговоров",
    },
    defaultSize: 2,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: false,
    component: SearchPanel,
    renderBar: SearchBarContent,
    panelMode: "modal",
  },
  {
    id: "ai-assistant",
    icon: Bot,
    label: { he: "עוזר AI", en: "AI Assistant", ru: "AI Ассистент" },
    description: {
      he: "עוזר AI מהיר עם הקשר לעמוד",
      en: "Quick AI assistant with page context",
      ru: "Быстрый AI-ассистент с контекстом страницы",
    },
    defaultSize: 1,
    status: "active",
    category: "ai_comms",
    tier: "free",
    isRemovable: true,
    component: AIPanel,
    renderBar: AIBarContent,
    panelMode: "side-panel",
    placement: "both",
  },
  {
    id: "quick-create",
    icon: Plus,
    label: { he: "יצירה מהירה", en: "Quick Create", ru: "Быстрое создание" },
    description: {
      he: "צור מסמך או ישות חדשה",
      en: "Create a document or new entity",
      ru: "Создать документ или новую сущность",
    },
    defaultSize: 2,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: true,
    component: QuickCreatePanel,
    renderBar: QuickCreateBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "defaultType",
        label: { he: "סוג ברירת מחדל", en: "Default type" },
        type: "select",
        default: "note",
        options: [
          { value: "note", label: { he: "הערה", en: "Note" } },
          { value: "task", label: { he: "משימה", en: "Task" } },
          { value: "project", label: { he: "פרויקט", en: "Project" } },
        ],
      },
    ],
  },
  {
    // Opens the SmartNoteComposer, which is mounted once in DashboardShell and
    // listens for cc-open-widget-smart-note. Deliberately has no `component`:
    // the composer is a self-managed modal, so registering it as a panel too
    // would mount a second copy and open two modals on the same event.
    id: "smart-note",
    icon: Sparkles,
    label: { he: "פתק חכם", en: "Smart Note", ru: "Умная заметка" },
    description: {
      he: "כתוב פתק — נשמר מיד ומנותח על ידי AI",
      en: "Write a note — saved instantly, then analyzed by AI",
      ru: "Напишите заметку — сохраняется сразу, затем анализируется ИИ",
    },
    defaultSize: 1,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: true,
    placement: "topbar",
  },
  {
    id: "favorites",
    icon: Pin,
    label: { he: "מועדפים", en: "Favorites", ru: "Избранное" },
    description: {
      he: "דפים וקישורים שנשמרו",
      en: "Saved pages and links",
      ru: "Сохранённые страницы и ссылки",
    },
    defaultSize: 1,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: true,
    component: FavoritesPanel,
    renderBar: FavoritesBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "maxItems",
        label: { he: "מספר מועדפים מקסימלי", en: "Max favorites" },
        description: { he: "כמה פריטים להציג בפאנל", en: "How many items to show in the panel" },
        type: "number",
        default: 10,
      },
    ],
  },
  {
    id: "today",
    icon: Calendar,
    label: { he: "היום", en: "Today", ru: "Сегодня" },
    description: {
      he: "פגישות, דדליינים ותזכורות",
      en: "Meetings, deadlines & reminders",
      ru: "Встречи, дедлайны и напоминания",
    },
    defaultSize: 2,
    status: "active",
    category: "productivity",
    tier: "free",
    isRemovable: true,
    component: TodayPanel,
    renderBar: TodayBarContent,
    placement: "both",
    // Canary settings schema — first widget to demonstrate the WidgetFieldSchema
    // pipeline. SidebarWidgetInspector renders a settings editor for these.
    // TodayPanel can read these via useWidgetSettings("today", schema) in a
    // follow-up to actually apply them at render time.
    settingsSchema: [
      {
        key: "showWeekends",
        label: { he: "הצג סופי שבוע", en: "Show weekends" },
        description: { he: "כלול שישי ושבת בתצוגה", en: "Include Fri+Sat in the view" },
        type: "boolean",
        default: true,
      },
      {
        key: "maxItems",
        label: { he: "מספר פריטים מקסימלי", en: "Max items" },
        description: { he: "כמה פגישות/דדליינים להציג", en: "How many meetings/deadlines to show" },
        type: "number",
        default: 5,
      },
      {
        key: "timeFormat",
        label: { he: "פורמט שעה", en: "Time format" },
        type: "select",
        default: "24h",
        options: [
          { value: "24h", label: { he: "24 שעות", en: "24-hour" } },
          { value: "12h", label: { he: "12 שעות (AM/PM)", en: "12-hour (AM/PM)" } },
        ],
      },
    ],
  },
  {
    id: "notifications",
    icon: Bell,
    label: { he: "התראות", en: "Notifications", ru: "Уведомления" },
    description: {
      he: "התראות מפרויקטים, מסמכים ו-AI",
      en: "Notifications from projects, documents & AI",
      ru: "Уведомления от проектов, документов и AI",
    },
    defaultSize: 1,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: true,
    component: NotificationsPanel,
    renderBar: NotificationsBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "maxItems",
        label: { he: "מספר התראות מקסימלי", en: "Max notifications" },
        description: { he: "כמה התראות להציג בפאנל", en: "How many notifications to show in the panel" },
        type: "number",
        default: 20,
      },
      {
        key: "unreadOnly",
        label: { he: "רק שלא נקראו", en: "Unread only" },
        description: { he: "סנן והצג רק התראות שלא נקראו", en: "Filter to show only unread items" },
        type: "boolean",
        default: false,
      },
    ],
  },
  {
    id: "timer",
    icon: Clock,
    label: { he: "טיימר", en: "Timer", ru: "Таймер" },
    description: {
      he: "טיימר פומודורו לניהול זמן",
      en: "Pomodoro timer for time management",
      ru: "Таймер Помодоро для управления временем",
    },
    defaultSize: 2,
    status: "active",
    category: "productivity",
    tier: "free",
    isRemovable: true,
    component: TimerPanel,
    renderBar: TimerBarContent,
  },
  {
    id: "clipboard",
    icon: ClipboardList,
    label: { he: "לוח הדבקות", en: "Clipboard", ru: "Буфер обмена" },
    description: {
      he: "פריטים שהועתקו לאחרונה",
      en: "Recently copied items",
      ru: "Недавно скопированные элементы",
    },
    defaultSize: 1,
    status: "active",
    category: "productivity",
    tier: "free",
    isRemovable: true,
    component: ClipboardPanel,
    renderBar: ClipboardBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "maxItems",
        label: { he: "מספר פריטים מקסימלי", en: "Max items" },
        type: "number",
        default: 20,
      },
      {
        key: "autoClearMinutes",
        label: { he: "ניקוי אוטומטי (דקות)", en: "Auto-clear after (minutes)" },
        description: { he: "0 = ללא ניקוי אוטומטי", en: "0 = never auto-clear" },
        type: "number",
        default: 0,
      },
    ],
  },
  {
    id: "settings",
    icon: Settings,
    label: { he: "הגדרות", en: "Settings", ru: "Настройки" },
    description: {
      he: "השהיית ריחוף + גישה מהירה להגדרות",
      en: "Hover delay + quick access to settings",
      ru: "Задержка наведения + быстрый доступ к настройкам",
    },
    defaultSize: 1,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: false,
    component: SettingsPanel,
    renderBar: SettingsBarContent,
  },
  {
    id: "keyboard-shortcuts",
    icon: Keyboard,
    label: { he: "קיצורי מקלדת", en: "Keyboard Shortcuts", ru: "Горячие клавиши" },
    description: {
      he: "קיצורי מקלדת וניהול פעולות",
      en: "Keyboard shortcuts & action management",
      ru: "Горячие клавиши и управление действиями",
    },
    defaultSize: 1,
    status: "active",
    category: "basics",
    tier: "free",
    isRemovable: true,
    component: ShortcutsPanel,
    renderBar: ShortcutsBarContent,
    panelMode: "modal",
  },
  {
    id: "weekly-planner",
    icon: CalendarDays,
    label: { he: "סדר שבועי", en: "Weekly Planner", ru: "Еженедельный план" },
    description: {
      he: "תכנון שבועי, משימות יומיות ותבניות חוזרות",
      en: "Weekly planning, daily tasks & recurring templates",
      ru: "Еженедельное планирование, ежедневные задачи и повторяющиеся шаблоны",
    },
    defaultSize: 2,
    status: "active",
    category: "productivity",
    tier: "pro",
    isRemovable: true,
    component: WeeklyPlannerPanel,
    renderBar: WeeklyPlannerBarContent,
    panelMode: "modal",
    placement: "both",
    settingsSchema: [
      {
        key: "showCompleted",
        label: { he: "הצג שהושלמו", en: "Show completed" },
        description: { he: "כלול משימות שכבר הושלמו", en: "Include already-completed tasks" },
        type: "boolean",
        default: true,
      },
      // weekStartsOn: TODO — requires WeeklyPlannerProvider/getWeekDates to
      // accept a startDay parameter. Defer until that refactor.
    ],
  },
  // ─── Communications & Team ────────────────────────────────────
  {
    id: "wati",
    icon: MessageCircle,
    label: { he: "הודעות WATI", en: "WATI Messages", ru: "Сообщения WATI" },
    description: {
      he: "הודעות WhatsApp אחרונות שצריכות תשומת לב",
      en: "Recent WhatsApp messages needing attention",
      ru: "Последние сообщения WhatsApp, требующие внимания",
    },
    defaultSize: 2,
    status: "active",
    category: "ai_comms",
    tier: "pro",
    isRemovable: true,
    component: WATIPanel,
    renderBar: WATIBarContent,
    placement: "both",
  },
  {
    id: "wati-iframe",
    icon: ExternalLink,
    label: { he: "WATI Dashboard", en: "WATI Dashboard", ru: "WATI Dashboard" },
    description: {
      he: "גישה ישירה ל-WATI Dashboard דרך iframe מוטבע",
      en: "Direct access to WATI Dashboard through embedded iframe",
      ru: "Прямой доступ к WATI Dashboard через встроенный iframe",
    },
    defaultSize: 3,
    status: "active",
    category: "integrations",
    tier: "pro",
    isRemovable: true,
    component: WATIIframePanel,
    renderBar: WATIIframeBarContent,
    placement: "both",
  },
  {
    id: "team",
    icon: Users,
    label: { he: "צוות", en: "Team", ru: "Команда" },
    description: {
      he: "מי online, מי עובד על מה",
      en: "Who's online, who's working on what",
      ru: "Кто онлайн, кто над чем работает",
    },
    defaultSize: 2,
    status: "active",
    category: "team",
    tier: "business",
    isRemovable: true,
    component: TeamPanel,
    renderBar: TeamBarContent,
    placement: "both",
  },
  {
    id: "kpi",
    icon: BarChart3,
    label: { he: "מדדים", en: "KPIs", ru: "Показатели" },
    description: {
      he: "מדדים מהירים — פרויקטים פתוחים, מסמכים, בריאות",
      en: "Quick metrics — open projects, documents, health",
      ru: "Быстрые метрики — открытые проекты, документы, здоровье",
    },
    defaultSize: 2,
    status: "active",
    category: "analytics",
    tier: "free",
    isRemovable: true,
    component: KPIPanel,
    renderBar: KPIBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "refreshIntervalSec",
        label: { he: "רענון אוטומטי (שניות)", en: "Auto-refresh (seconds)" },
        description: { he: "0 = ידני בלבד", en: "0 = manual refresh only" },
        type: "number",
        default: 60,
      },
      {
        key: "showTrends",
        label: { he: "הצג טרנדים", en: "Show trends" },
        type: "boolean",
        default: true,
      },
    ],
  },
  {
    id: "leads-pipeline",
    icon: TrendingUp,
    label: { he: "צנרת לידים", en: "Leads Pipeline", ru: "Воронка лидов" },
    description: {
      he: "שלבי צנרת מכירות — תצוגת פאנל אופקית",
      en: "Sales pipeline stages — horizontal funnel view",
      ru: "Этапы воронки продаж — горизонтальный вид воронки",
    },
    defaultSize: 2,
    status: "active",
    category: "analytics",
    tier: "free",
    isRemovable: true,
    component: LeadsPipelinePanel,
    renderBar: LeadsPipelineBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "stageFilter",
        label: { he: "סנן לפי שלב", en: "Filter by stage" },
        type: "select",
        default: "all",
        options: [
          { value: "all", label: { he: "כל השלבים", en: "All stages" } },
          { value: "new", label: { he: "חדש", en: "New" } },
          { value: "qualified", label: { he: "מסונן", en: "Qualified" } },
          { value: "proposal", label: { he: "הצעה", en: "Proposal" } },
          { value: "closed", label: { he: "נסגר", en: "Closed" } },
        ],
      },
    ],
  },
  {
    id: "matching",
    icon: Sparkles,
    label: { he: "התאמות", en: "Matching", ru: "Совпадения" },
    description: {
      he: "התאמות AI בין ישויות — עובדים, פרויקטים, קבלנים",
      en: "AI matching between entities — workers, projects, contractors",
      ru: "AI-сопоставление сущностей — работники, проекты, подрядчики",
    },
    defaultSize: 2,
    status: "active",
    category: "analytics",
    tier: "pro",
    isRemovable: true,
    component: MatchingPanel,
    renderBar: MatchingBarContent,
    panelMode: "side-panel",
    placement: "both",
  },
  {
    id: "rss",
    icon: Rss,
    label: { he: "עדכוני RSS", en: "RSS Feeds", ru: "RSS-ленты" },
    description: {
      he: "חדשות נדל\"ן ובנייה — כותרות אחרונות",
      en: "Construction & real estate news — latest headlines",
      ru: "Новости строительства и недвижимости — последние заголовки",
    },
    defaultSize: 1,
    status: "active",
    category: "integrations",
    tier: "free",
    isRemovable: true,
    component: RssPanel,
    renderBar: RssBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "maxItems",
        label: { he: "מספר כותרות מקסימלי", en: "Max headlines" },
        type: "number",
        default: 15,
      },
      {
        key: "refreshIntervalMin",
        label: { he: "רענון אוטומטי (דקות)", en: "Auto-refresh (minutes)" },
        type: "number",
        default: 30,
      },
    ],
  },
  {
    id: "ceo-queue",
    icon: ListTodo,
    label: { he: "תור CEO", en: "CEO Queue", ru: "Очередь CEO" },
    description: {
      he: "תור הנחיות מנכ״ל — בקשות, עדיפויות, סטטוס ביצוע",
      en: "CEO directive queue — requests, priorities, execution status",
      ru: "Очередь директив CEO — запросы, приоритеты, статус выполнения",
    },
    defaultSize: 1,
    status: "active",
    category: "analytics",
    tier: "free",
    isRemovable: true,
    component: CeoQueuePanel,
    renderBar: CeoQueueBarContent,
    placement: "both",
    settingsSchema: [
      {
        key: "priorityFilter",
        label: { he: "סנן לפי עדיפות", en: "Filter by priority" },
        type: "select",
        default: "all",
        options: [
          { value: "all", label: { he: "כל העדיפויות", en: "All" } },
          { value: "high", label: { he: "גבוהה", en: "High" } },
          { value: "medium", label: { he: "בינונית", en: "Medium" } },
          { value: "low", label: { he: "נמוכה", en: "Low" } },
        ],
      },
      {
        key: "hideCompleted",
        label: { he: "הסתר שהושלמו", en: "Hide completed" },
        type: "boolean",
        default: true,
      },
    ],
  },
  {
    id: "communication",
    icon: MessagesSquare,
    label: { he: "תקשורת", en: "Communication", ru: "Коммуникация" },
    description: {
      he: "ציר זמן תקשורת — וואטסאפ, טלפון, מייל, הערות",
      en: "Communication timeline — WhatsApp, phone, email, notes",
      ru: "Лента коммуникации — WhatsApp, телефон, почта, заметки",
    },
    defaultSize: 2,
    status: "active",
    category: "ai_comms",
    tier: "pro",
    isRemovable: true,
    component: CommunicationWidgetPanel,
    renderBar: CommunicationBarContent,
    panelMode: "side-panel",
    placement: "both",
    settingsSchema: [
      {
        key: "maxItems",
        label: { he: "מספר אירועים מקסימלי", en: "Max events" },
        description: { he: "כמה אירועי תקשורת להציג בציר הזמן", en: "How many communication events to show on the timeline" },
        type: "number",
        default: 30,
      },
      {
        key: "groupByContact",
        label: { he: "קבץ לפי איש קשר", en: "Group by contact" },
        description: { he: "אגד אירועים מאותו איש קשר ביחד", en: "Bundle events from the same contact together" },
        type: "boolean",
        default: false,
      },
    ],
  },
  {
    id: "email-stats",
    icon: Mail,
    label: { he: "סטטיסטיקות מייל", en: "Email Stats", ru: "Статистика почты" },
    description: {
      he: "סטטיסטיקות שליחת מיילים — נשלחו, נפתחו, קליקים, חזרות",
      en: "Email sending stats — sent, opened, clicked, bounced",
      ru: "Статистика рассылки — отправлено, открыто, клик, отклонено",
    },
    defaultSize: 2,
    status: "active",
    category: "analytics",
    tier: "pro",
    isRemovable: true,
    component: EmailStatsPanel,
    renderBar: EmailStatsBarContent,
    placement: "both",
  },
  {
    id: "gmail",
    icon: Inbox,
    label: { he: "Gmail", en: "Gmail", ru: "Gmail" },
    description: {
      he: "תיבת דואר Gmail — הודעות אחרונות ותקצירים",
      en: "Gmail inbox — recent messages and summaries",
      ru: "Почта Gmail — последние письма и сводки",
    },
    defaultSize: 2,
    status: "active",
    category: "integrations",
    tier: "pro",
    isRemovable: true,
    component: GmailPanel,
    renderBar: GmailBarContent,
    placement: "both",
  },
  {
    id: "google-calendar",
    icon: CalendarDays,
    label: { he: "Google Calendar", en: "Google Calendar", ru: "Google Календарь" },
    description: {
      he: "לוח שנה Google — פגישות ואירועים קרובים",
      en: "Google Calendar — upcoming meetings and events",
      ru: "Google Календарь — предстоящие встречи и события",
    },
    defaultSize: 2,
    status: "active",
    category: "integrations",
    tier: "pro",
    isRemovable: true,
    component: CalendarWidgetPanel,
    renderBar: CalendarBarContent,
    placement: "both",
  },
  {
    id: "shortcuts",
    icon: ExternalLink,
    label: { he: "קיצורים", en: "Quick Links", ru: "Ссылки" },
    description: {
      he: "לינקים מהירים לאוריגמי, Notion, WATI, n8n",
      en: "Quick links to Origami, Notion, WATI, n8n",
      ru: "Быстрые ссылки на Origami, Notion, WATI, n8n",
    },
    defaultSize: 1,
    status: "active",
    category: "integrations",
    tier: "free",
    isRemovable: true,
    component: ExternalLinksPanel,
    renderBar: ExternalLinksBarContent,
    placement: "both",
  },
  {
    id: "page-guide",
    icon: Sparkles,
    label: { he: "מדריך עמוד", en: "Page Guide", ru: "Гид по странице" },
    description: {
      he: "עוזר חכם שמכיר את העמוד הנוכחי ומציע איזה כלים יעזרו לך",
      en: "Smart assistant that knows the current page and suggests which tools can help",
      ru: "Умный помощник, знающий текущую страницу и предлагающий подходящие инструменты",
    },
    defaultSize: 2,
    status: "active",
    category: "ai_comms",
    tier: "free",
    isRemovable: true,
    component: PageGuidePanel,
    renderBar: PageGuideRenderBar,
    panelMode: "side-panel",
    placement: "sidebar-rail",
  },
  {
    id: "capabilities",
    icon: Library,
    label: { he: "ספריית היכולות", en: "Capability Library", ru: "Библиотека возможностей" },
    description: {
      he: "סקילים ופרומפטים במקום אחד — חיפוש והעתקה בלי לעזוב את המסך",
      en: "Skills and prompts in one place — search and copy without leaving the page",
      ru: "Навыки и промпты в одном месте — поиск и копирование не покидая страницу",
    },
    defaultSize: 2,
    status: "active",
    category: "ai_comms",
    tier: "free",
    isRemovable: true,
    component: CapabilitiesPanel,
    renderBar: CapabilitiesBarContent,
    panelMode: "side-panel",
    placement: "both",
  },
];

export function getWidgetById(id: string): WidgetDefinition | undefined {
  return widgetRegistry.find((w) => w.id === id);
}
